import { FirmwareType, FirmwareWithMetadata } from './firmware-metadata.model';

/**
 * Known Solarman product IDs for firmware management
 */
export const FIRMWARE_PRODUCT_IDS = [
  { id: '0_D0002_18', label: 'Logger', type: 'logger' as FirmwareType },
  { id: '0_270A_1', label: 'Inverter V3', type: 'inverter' as FirmwareType },
  { id: '0_2708_1', label: 'Inverter V1 (HYD 3-6K-ES)', type: 'inverter' as FirmwareType },
  { id: '0_2703_1', label: 'Inverter V1 (ME3000SP)', type: 'inverter' as FirmwareType },
  { id: '0_2706_1', label: 'Inverter V1 (50-70KTL)', type: 'inverter' as FirmwareType },
  { id: '0_2701_1', label: 'Inverter V1 (1-40kW)', type: 'inverter' as FirmwareType },
] as const;

/**
 * Factory tags from Solarman system (uppercase convention)
 */
export const FACTORY_TAGS = ['OFFICIAL_RELEASE', 'BACKUP_RELEASE', 'PREVIOUS', 'CURRENT', 'ONE_CLICK'] as const;

/**
 * Suggested firmware tags (hardcoded)
 * Operators can select these or add custom tags
 * Includes both factory tags and operational tags
 */
export const SUGGESTED_FIRMWARE_TAGS = [
  // Factory tags
  ...FACTORY_TAGS,
] as const;

export type SuggestedFirmwareTag = typeof SUGGESTED_FIRMWARE_TAGS[number];

/**
 * Check if a tag is a suggested tag
 */
export function isSuggestedTag(tag: string): tag is SuggestedFirmwareTag {
  return SUGGESTED_FIRMWARE_TAGS.includes(tag as SuggestedFirmwareTag);
}

/**
 * Sort tags: suggested tags first, then custom tags alphabetically
 */
export function sortTags(tags: string[]): string[] {
  const suggested: string[] = [];
  const custom: string[] = [];

  for (const tag of tags) {
    if (isSuggestedTag(tag)) {
      suggested.push(tag);
    } else {
      custom.push(tag);
    }
  }

  // Sort suggested by their order in SUGGESTED_FIRMWARE_TAGS
  suggested.sort((a, b) => {
    const indexA = SUGGESTED_FIRMWARE_TAGS.indexOf(a as SuggestedFirmwareTag);
    const indexB = SUGGESTED_FIRMWARE_TAGS.indexOf(b as SuggestedFirmwareTag);
    return indexA - indexB;
  });

  // Sort custom alphabetically
  custom.sort((a, b) => a.localeCompare(b));

  return [...suggested, ...custom];
}

/**
 * Logger type definition with version regex patterns
 * versionRegex: patterns that match firmware version strings (from upgrade-config.json / firmware-metadata.json)
 */
export interface LoggerTypeEntry {
  label: string;
  familyIds: string[];
  versionRegex: string[];
}

/**
 * Logger types with version regex associations
 * Sources: firmware-metadata.json (versionRegex), upgrade-config.json (keys), Excel (estimatedModel)
 */
export const LOGGER_TYPES: LoggerTypeEntry[] = [
  { label: 'LSE-4W',         familyIds: [],                                              versionRegex: [] },
  { label: 'ETH-USB',        familyIds: ['ME_0D_270A', 'ME_0E_ZCS_270A', 'ME_0E_270A'], versionRegex: ['^me_0d_270a([_.]|[0-9a-z])+$', '^me_0e_zcs_270a([_.]|[0-9a-z])+$', '^me_0e_270a([_.]|[0-9a-z])+$'] },
  { label: 'WIFI-USB',       familyIds: ['LSW3_15_MQTT_270A', 'LSW3_ZCSMQTT_270A', 'LSW3_15_270A'], versionRegex: ['^lsw3_15_mqtt_270a([_.]|[0-9a-z])+$', '^lsw3_zcsmqtt_270a([_.]|[0-9a-z])+$', '^lsw3_15_270a([_.]|[1-9a-z])+$'] },
  { label: '4G-USB',         familyIds: [],                                              versionRegex: [] },
  { label: 'GPRS-USB',       familyIds: [],                                              versionRegex: [] },
  { label: 'ETH-DB9',        familyIds: ['ME_0D_2701'],                                  versionRegex: ['^me_0d_2701([_.]|[0-9a-z])+$'] },
  { label: 'WIFI-DB9',       familyIds: ['LSW3_15_FFFF'],                                versionRegex: ['^lsw3_15_ffff([_.]|[0-9a-z])+$'] },
  { label: '4G-DB9',         familyIds: [],                                              versionRegex: [] },
  { label: 'GPRS-DB9',       familyIds: [],                                              versionRegex: [] },
  { label: 'DATALOG-04/10',  familyIds: [],                                              versionRegex: [] },
  { label: 'ZSM-METER-3PH', familyIds: [],                                              versionRegex: [] },
  { label: 'ZSM-METER-1PH', familyIds: [],                                              versionRegex: [] },
];

/**
 * Inverter serial pattern definition with serial regex
 * serialRegex: pattern that matches inverter serial numbers (from upgrade-config.json / firmware-metadata.json)
 */
export interface InverterSerialPatternEntry {
  label: string;
  familyId: string;
  modelFamily: string;
  serialRegex: string[];
}

/**
 * Inverter serial patterns with serial regex and model family associations
 * Sources: firmware-metadata.json (serialRegex), upgrade-config.json (keys), Excel (inverter serial + model family)
 * Short format (14 chars): ZXXxxxxxxxxxxx / Long format (20 chars): Zx####xxxxxxxxxxxxxx
 */
export const INVERTER_SERIAL_PATTERNS: InverterSerialPatternEntry[] = [
  { label: 'ZA3xxxxxxxxxxx',        familyId: 'ZA3',      modelFamily: '1PH 1100TL-V3 / 3300TL-V3',                       serialRegex: [] },
  { label: 'Zx1071xxxxxxxxxxxxxx',  familyId: 'Zx1071',   modelFamily: '1PH 1100-5000TL2-V3',                             serialRegex: [] },
  { label: 'ZH3xxxxxxxxxxx',        familyId: 'ZH3',      modelFamily: '1PH 3000TLM-V3 / 6000TLM-V3',                     serialRegex: ['^zh3.{11}$'] },
  { label: 'ZH1xxxxxxxxxxx',        familyId: 'ZH1',      modelFamily: '1PH 3000TLM-V2 / 6000TLM-V2',                     serialRegex: [] },
  { label: 'ZFxxxxxxxxxxxx',        familyId: 'ZF',       modelFamily: '3.3KTL-V1 / 12KTL-V1',                            serialRegex: [] },
  { label: 'ZS2xxxxxxxxxxx',        familyId: 'ZS2',      modelFamily: '3PH 3.3KTL-V3 / 12KTL-V3',                        serialRegex: ['^zs2.{11}$'] },
  { label: 'ZS1xxxxxxxxxxx',        familyId: 'ZS1',      modelFamily: '3PH 15000TL-V3 / 24000TL-V3',                     serialRegex: ['^zs1.{11}$'] },
  { label: 'ZS3xxxxxxxxxxx',        familyId: 'ZS3',      modelFamily: '3PH 25KTL-V3 / 50KTL-V3',                         serialRegex: ['^zs3.{11}$'] },
  { label: 'Zx1021xxxxxxxxxxxxxx',  familyId: 'Z_1021',   modelFamily: '60KTL-V3 / 3PH 80KTL-V3',                         serialRegex: ['^z.1021.{14}$'] },
  { label: 'ZQ1xxxxxxxxxxx',        familyId: 'ZQ1',      modelFamily: '80KTL-LV / 136KTL-HV',                            serialRegex: ['^zq1.{11}$'] },
  { label: 'Zx1036xxxxxxxxxxxxxx',  familyId: 'Z_1036',   modelFamily: '100KTL-V4 / 110KTL-V4',                           serialRegex: ['^z.1036.{14}$'] },
  { label: 'ZR1xxxxxxxxxxx',        familyId: 'ZR1',      modelFamily: '3PH 250KTL-HV / 255KTL-HV',                       serialRegex: ['^zr1.{11}$'] },
  { label: 'Zx1030xxxxxxxxxxxxxx',  familyId: 'Z_1030',   modelFamily: '3PH 250-350KTL-HV Z0',                            serialRegex: ['^z.1030.{14}$'] },
  { label: 'ZL1xxxxxxxxxxx',        familyId: 'ZL1',      modelFamily: '20000TL-V2 / 33000TL-V2',                         serialRegex: [] },
  { label: 'ZJxxxxxxxxxxxx',        familyId: 'ZJ',       modelFamily: '50000TL-V1 / 60000TL-V1',                         serialRegex: ['^zj.{12}$'] },
  { label: 'ZE1xxxxxxxxxxx',        familyId: 'ZE',       modelFamily: 'ME3000SP',                                        serialRegex: ['^ze.{12}$'] },
  { label: 'ZM1xxxxxxxxxxx',        familyId: 'ZM1_old',  modelFamily: 'HYD 3000-6000 ZSS',                               serialRegex: ['^zm1.{5}[a-k][1-9a-z].{4}$', '^zm1.{5}[l-z][1-9a-z].{4}$'] },
  { label: 'ZM2xxxxxxxxxxx',        familyId: 'ZM2',      modelFamily: 'HYD 3000-6000 ZSS HP',                            serialRegex: ['^zm2.{11}$'] },
  { label: 'Zx1050xxxxxxxxxxxxxx',  familyId: 'ZM2',      modelFamily: 'HYD 3000-6000 ZSS HP',                            serialRegex: ['^z.1050.{14}$'] },
  { label: 'Zx1025xxxxxxxxxxxxxx',  familyId: 'Z_1025',   modelFamily: 'HYD 3000-6000 ZP1',                               serialRegex: ['^z.1025.{14}$'] },
  { label: 'ZP1xxx (old)',            familyId: 'ZP1_OLD',  modelFamily: 'HYD 5000-20000 ZSS (old HW)',                     serialRegex: ['^zp1.{5}([a-l][1-9a-c]|m[1-5]).{4}$'] },
  { label: 'ZP1xxxxxxxxxxx',        familyId: 'ZP1',      modelFamily: 'HYD 5000-20000 ZSS',                              serialRegex: ['^zp1.{5}(m[6-9a-c]|[n-z][1-9a-c]).{4}$'] },
  { label: 'ZP2xxxxxxxxxxx',        familyId: 'ZP2',      modelFamily: 'HYD 5000-20000 ZSS',                              serialRegex: ['^zp2.{11}$'] },
  { label: 'Zx1057xxxxxxxxxxxxxx',  familyId: 'Z_1057',   modelFamily: '3PH HYD 5000-12000 ZP3',                          serialRegex: ['^z.1057.{14}$'] },
];

/**
 * Detect logger type from module version string.
 * Matches against LOGGER_TYPES versionRegex patterns.
 */
export function detectLoggerType(moduleVersion: string): string | null {
  if (!moduleVersion) {
    return null;
  }
  const lowerVersion = moduleVersion.toLowerCase();
  for (const entry of LOGGER_TYPES) {
    for (const pattern of entry.versionRegex) {
      if (new RegExp(pattern).test(lowerVersion)) {
        return entry.label;
      }
    }
  }
  return null;
}

/**
 * Detect matching inverter serial pattern labels.
 * Matches serial against INVERTER_SERIAL_PATTERNS serialRegex patterns.
 */
export function detectInverterPatternLabels(serial: string): string[] {
  if (!serial) {
    return [];
  }
  const lowerSerial = serial.toLowerCase();
  const matched: string[] = [];
  for (const entry of INVERTER_SERIAL_PATTERNS) {
    for (const pattern of entry.serialRegex) {
      if (new RegExp(pattern).test(lowerSerial)) {
        matched.push(entry.label);
        break;
      }
    }
  }
  return matched;
}

/**
 * Filter firmware list to only those compatible with detected logger type.
 * Only includes firmware WITH metadata and matching loggerType.
 */
export function filterFirmwareByLoggerType(
  fwList: FirmwareWithMetadata[],
  detectedLoggerType: string | null,
): FirmwareWithMetadata[] {
  if (!detectedLoggerType) {
    return [];
  }
  return fwList.filter((fw) => fw.metadata && fw.metadata.loggerType === detectedLoggerType);
}

/**
 * Filter firmware list to only those compatible with detected serial patterns.
 * Only includes firmware WITH metadata and matching serialPatterns.
 */
export function filterFirmwareBySerialPatterns(
  fwList: FirmwareWithMetadata[],
  matchedPatternLabels: string[],
): FirmwareWithMetadata[] {
  if (!matchedPatternLabels.length) {
    return [];
  }
  return fwList.filter(
    (fw) => fw.metadata?.serialPatterns?.some((sp) => matchedPatternLabels.includes(sp)),
  );
}

/**
 * Normalize tag (lowercase, trim, replace spaces with dashes)
 */
export function normalizeTag(tag: string): string {
  return tag.toLowerCase().trim().replace(/\s+/g, '-');
}

/**
 * Validate tag format
 */
export function isValidTag(tag: string): boolean {
  if (!tag || tag.length === 0) {
    return false;
  }
  // Allow alphanumeric (upper and lower), dashes, and underscores
  const tagRegex = /^[a-zA-Z0-9-_]+$/;
  return tagRegex.test(tag.trim());
}
